import React from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../../../../utils/api";
import Pagination from "../../../../components/Pagination";
import StatusBadge from "./StatusBadge";
import LoadingSpinner from "./LoadingSpinner";
import EmptyState from "./EmptyState";
import SortIcon from "./SortIcon";
import { getProductImageUrl } from "../utils/productUtils";
import { SORTABLE_FIELDS, TABLE_HEADERS } from "../utils/constants";

/**
 * Product Table Component
 * Displays products in a table with sorting and pagination
 */
const ProductTable = ({
  products,
  loading,
  page,
  totalPages,
  startIndex,
  onPageChange,
  onSort,
  searchQuery,
}) => {
  const navigate = useNavigate();

  const handleRowClick = (productId) => {
    navigate(`/product/productdetail/${productId}`);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (products.length === 0) {
    return <EmptyState searchQuery={searchQuery} />;
  }

  return (
    <div className="h-full flex flex-col bg-white rounded-lg shadow-sm overflow-hidden">
      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-black sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                {TABLE_HEADERS.NUMBER}
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                {TABLE_HEADERS.IMAGE}
              </th>
              <th
                onClick={() => onSort(SORTABLE_FIELDS.PRODUCT_NAME)}
                className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-gray-800"
              >
                <div className="flex items-center gap-1.5">
                  {TABLE_HEADERS.PRODUCT_NAME}
                  <SortIcon />
                </div>
              </th>
              <th
                onClick={() => onSort(SORTABLE_FIELDS.PRODUCT_NUMBER)}
                className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-gray-800"
              >
                <div className="flex items-center gap-1.5">
                  {TABLE_HEADERS.PRODUCT_NUMBER}
                  <SortIcon />
                </div>
              </th>
              <th
                onClick={() => onSort(SORTABLE_FIELDS.CATEGORY)}
                className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-gray-800"
              >
                <div className="flex items-center gap-1.5">
                  {TABLE_HEADERS.CATEGORY}
                  <SortIcon />
                </div>
              </th>
              <th
                onClick={() => onSort(SORTABLE_FIELDS.SUBCATEGORY)}
                className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-gray-800"
              >
                <div className="flex items-center gap-1.5">
                  {TABLE_HEADERS.SUBCATEGORY}
                  <SortIcon />
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                {TABLE_HEADERS.DESCRIPTION}
              </th>
              <th
                onClick={() => onSort(SORTABLE_FIELDS.STATUS)}
                className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-gray-800"
              >
                <div className="flex items-center gap-1.5">
                  {TABLE_HEADERS.STATUS}
                  <SortIcon />
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {products.map((product, index) => (
              <tr
                key={product._id}
                onClick={() => handleRowClick(product._id)}
                className="h-[50px] hover:bg-gradient-to-r hover:from-gray-50 hover:to-white transition-all duration-200 cursor-pointer hover:shadow-sm"
              >
                <td className="px-4 py-2 whitespace-nowrap">
                  <span className="text-gray-600 font-medium text-xs">
                    {startIndex + index + 1}
                  </span>
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  <img
                    src={getProductImageUrl(product, API_BASE)}
                    alt={product.productName}
                    className="w-12 h-12 object-cover rounded-md border border-gray-200"
                  />
                </td>
                <td className="px-4 py-2">
                  <span className="font-semibold text-gray-900 text-xs">
                    {product.productName}
                  </span>
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  <span className="text-blue-600 font-medium text-xs">
                    {product.productNumber}
                  </span>
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  <span className="text-gray-700 text-xs">
                    {product.categoryName}
                  </span>
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  <span className="text-gray-700 text-xs">
                    {product.subcategoryName}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <span className="text-gray-600 text-xs line-clamp-2">
                    {product.description || "No description"}
                  </span>
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  <StatusBadge status={product.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="border-t border-gray-100 px-6 py-4">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
};

export default ProductTable;
